// Petsmate formulary — Part B (50 conditions) — see diseases_A.js for schema

export const DISEASES_B = [
  // ==================== DERMATOLOGY (part 2) ====================
  {
    id: "hot-spot",
    name: "Pyotraumatic Dermatitis (Hot Spot)",
    species: ["canine"],
    category: "Dermatology",
    clinicalSigns: "Acute, well-demarcated, moist, erythematous, painful lesion — self-inflicted from scratching / licking.",
    diagnostics: [
      { test: "Physical exam", finding: "Classic moist exudative alopecic lesion" },
      { test: "Skin cytology", finding: "Cocci + neutrophils; rule out deep pyoderma" },
      { test: "Search for trigger", finding: "Flea bite, otitis, atopy, anal sac disease" },
    ],
    differentials: ["Deep pyoderma", "Demodicosis", "Dermatophytosis", "Autoimmune skin disease"],
    medications: [
      { drug: "Clip + clean with chlorhexidine 2% scrub", doseMgPerKg: null, doseText: "Cleanse lesion", route: "Topical", frequency: "SID", duration: "5–7 days" },
      { drug: "Silver sulfadiazine cream", doseMgPerKg: null, doseText: "Thin layer", route: "Topical", frequency: "BID", duration: "7 days" },
      { drug: "Cephalexin", doseMgPerKg: 22, doseText: "22 mg/kg", route: "PO", frequency: "BID", duration: "7–14 days" },
      { drug: "Prednisolone (short taper)", doseMgPerKg: 0.5, doseText: "0.5 mg/kg", route: "PO", frequency: "SID × 3 d, EOD × 3 d", duration: "6 days" },
    ],
    supportiveCare: [
      "Elizabethan collar to prevent self-trauma.",
      "Identify & treat underlying trigger.",
      "Cool, dry environment.",
    ],
  },
  {
    id: "acral-lick",
    name: "Acral Lick Dermatitis",
    species: ["canine"],
    category: "Dermatology",
    clinicalSigns: "Firm alopecic thickened plaque on carpus / metatarsus from chronic licking. Labs, Dobies, Great Danes.",
    diagnostics: [
      { test: "Cytology + deep tissue culture", finding: "Deep bacterial infection (Staph, Pseudomonas) common" },
      { test: "Radiographs (chronic)", finding: "Rule out underlying arthritis, osteomyelitis, foreign body" },
      { test: "Behavioural / allergy work-up", finding: "Anxiety, atopy commonly underlie" },
    ],
    differentials: ["Neoplasia (mast cell, histiocytoma)", "Foreign body granuloma", "Fungal granuloma", "Cellulitis"],
    medications: [
      { drug: "Cephalexin (6–8 week course)", doseMgPerKg: 30, doseText: "22–30 mg/kg", route: "PO", frequency: "BID", duration: "6–8 weeks or per culture" },
      { drug: "Fluoxetine (compulsive component)", doseMgPerKg: 1, doseText: "1–2 mg/kg", route: "PO", frequency: "SID", duration: "8–12 weeks trial" },
      { drug: "Fluocinolone/DMSO gel (Synotic)", doseMgPerKg: null, doseText: "Thin film", route: "Topical", frequency: "BID", duration: "2 weeks" },
    ],
    supportiveCare: [
      "Prevent access — Elizabethan collar, socks.",
      "Environmental enrichment; behavioural modification.",
      "Long recurrence rate — set realistic expectations.",
    ],
  },
  {
    id: "feline-acne",
    name: "Feline Chin Acne",
    species: ["feline"],
    category: "Dermatology",
    clinicalSigns: "Comedones and papules on chin ± lower lip, sometimes progressing to folliculitis / furunculosis.",
    diagnostics: [
      { test: "Physical exam + skin cytology", finding: "Blackheads; secondary bacterial infection with cocci if severe" },
      { test: "Dermatophyte culture", finding: "Rule out ringworm" },
    ],
    differentials: ["Dermatophytosis", "Demodicosis (rare cats)", "Eosinophilic granuloma", "Autoimmune"],
    medications: [
      { drug: "Chlorhexidine 2% wipes", doseMgPerKg: null, doseText: "Cleanse chin", route: "Topical", frequency: "SID–BID", duration: "2–4 weeks" },
      { drug: "Mupirocin 2% ointment", doseMgPerKg: null, doseText: "Thin film", route: "Topical", frequency: "BID", duration: "10–14 days" },
      { drug: "Amoxicillin-Clavulanate (if furunculosis)", doseMgPerKg: 12.5, doseText: "12.5 mg/kg", route: "PO", frequency: "BID", duration: "14–21 days" },
    ],
    supportiveCare: [
      "Switch plastic bowls to stainless steel / ceramic.",
      "Clean chin after each meal.",
    ],
  },
  {
    id: "eos-granuloma",
    name: "Feline Eosinophilic Granuloma Complex",
    species: ["feline"],
    category: "Dermatology",
    clinicalSigns: "Indolent lip ulcer, eosinophilic plaques (pruritic, on ventral abdomen), linear granuloma (posterior thighs).",
    diagnostics: [
      { test: "Fine-needle aspirate / biopsy", finding: "Eosinophils dominant; rule out neoplasia" },
      { test: "CBC", finding: "Peripheral eosinophilia often present" },
      { test: "Flea combing + food/allergen workup", finding: "Underlying hypersensitivity in most cases" },
    ],
    differentials: ["Squamous cell carcinoma (lip ulcer)", "Mast cell tumour", "Cryptococcosis", "Bacterial granuloma"],
    medications: [
      { drug: "Prednisolone", doseMgPerKg: 2, doseText: "1–2 mg/kg", route: "PO", frequency: "SID, taper over 4 weeks", duration: "Until resolved, then lowest EOD" },
      { drug: "Methylprednisolone acetate (long-acting)", doseMgPerKg: null, doseText: "20 mg/cat", route: "IM / SC", frequency: "Every 2 weeks × 3", duration: "Non-compliant patients" },
      { drug: "Cyclosporine (refractory)", doseMgPerKg: 7, doseText: "7 mg/kg", route: "PO", frequency: "SID", duration: "6–8 weeks" },
      { drug: "Amoxicillin-Clavulanate (empirical trial)", doseMgPerKg: 12.5, doseText: "12.5 mg/kg", route: "PO", frequency: "BID", duration: "3 weeks" },
    ],
    supportiveCare: [
      "Rigorous flea prevention; hypoallergenic diet trial.",
      "Address underlying atopy / food allergy for long-term control.",
    ],
  },

  // ==================== NEUROLOGY ====================
  {
    id: "epilepsy",
    name: "Idiopathic Epilepsy",
    species: ["canine", "feline"],
    category: "Neurology",
    clinicalSigns: "Recurrent generalised tonic-clonic seizures, usually 1–5 y onset, otherwise normal between events.",
    diagnostics: [
      { test: "Complete neurological exam (inter-ictal)", finding: "Should be normal in idiopathic epilepsy" },
      { test: "CBC + biochem + bile acids + T4 + glucose", finding: "Exclude metabolic causes" },
      { test: "MRI + CSF analysis", finding: "Normal — rule out structural / inflammatory disease" },
      { test: "Pre-anticonvulsant drug levels (once therapy started)", finding: "Ensure therapeutic range" },
    ],
    differentials: ["Structural brain disease (tumour, MUE)", "Portosystemic shunt", "Hypoglycaemia (insulinoma)", "Toxin exposure", "Hepatic encephalopathy"],
    medications: [
      { drug: "Phenobarbital", doseMgPerKg: 2.5, doseText: "2.5 mg/kg", route: "PO", frequency: "BID", duration: "Life-long; recheck level 2 weeks" },
      { drug: "Levetiracetam", doseMgPerKg: 20, doseText: "20 mg/kg", route: "PO", frequency: "TID", duration: "Adjunct or first-line in cats" },
      { drug: "Potassium bromide (dog, add-on)", doseMgPerKg: 30, doseText: "30 mg/kg", route: "PO", frequency: "SID with food", duration: "Long term; do not use in cats" },
      { drug: "Diazepam per rectum (owner cluster protocol)", doseMgPerKg: 1, doseText: "0.5–1 mg/kg", route: "PR", frequency: "PRN cluster", duration: "Home rescue" },
    ],
    supportiveCare: [
      "Owner seizure diary; treat if >1 seizure/8 weeks or cluster.",
      "Recheck phenobarbital level + LFT every 6 months.",
      "Avoid metronidazole, fluoroquinolones with phenobarb.",
    ],
  },
  {
    id: "ivdd",
    name: "Intervertebral Disc Disease (IVDD)",
    species: ["canine"],
    category: "Neurology",
    clinicalSigns: "Acute back / neck pain, ataxia, paresis, paraplegia. Dachshunds, Beagles, Frenchies predisposed.",
    diagnostics: [
      { test: "Neurologic exam + reflexes + pain perception", finding: "Localise UMN vs LMN, functional grade (Modified Frankel 1–5)" },
      { test: "MRI (gold standard)", finding: "Extruded / protruded disc material, cord compression" },
      { test: "Myelography (if MRI unavailable)", finding: "Cord compression at affected level" },
      { test: "Spinal radiographs", finding: "Narrowed disc space, opacified foramina" },
    ],
    differentials: ["Fibrocartilaginous embolism", "Spinal tumour", "Discospondylitis", "Trauma"],
    medications: [
      { drug: "Gabapentin", doseMgPerKg: 10, doseText: "10–20 mg/kg", route: "PO", frequency: "TID", duration: "2–4 weeks" },
      { drug: "Methocarbamol (muscle relaxant)", doseMgPerKg: 20, doseText: "20 mg/kg", route: "PO", frequency: "TID", duration: "1–2 weeks" },
      { drug: "Meloxicam (only if not on steroids)", doseMgPerKg: 0.1, doseText: "0.1 mg/kg", route: "PO", frequency: "SID", duration: "5–7 days" },
      { drug: "Tramadol", doseMgPerKg: 5, doseText: "3–5 mg/kg", route: "PO", frequency: "TID PRN", duration: "1–2 weeks" },
    ],
    supportiveCare: [
      "STRICT cage rest 4–6 weeks (paresis, ambulatory).",
      "Refer for hemilaminectomy / ventral slot if non-ambulatory or progressive.",
      "Bladder expression, physiotherapy, gentle passive ROM.",
    ],
  },
  {
    id: "vestibular",
    name: "Idiopathic Vestibular Disease (Geriatric)",
    species: ["canine", "feline"],
    category: "Neurology",
    clinicalSigns: "Acute head tilt, nystagmus, ataxia, rolling, vomiting from disorientation. Older animals.",
    diagnostics: [
      { test: "Neurological exam", finding: "Peripheral signs (horizontal / rotary nystagmus, no CN deficits beyond CN VII/VIII)" },
      { test: "Otoscopic exam + BAER + head CT", finding: "Rule out otitis media/interna, nasopharyngeal polyp, mass" },
      { test: "CBC + biochem + T4", finding: "Rule out hypothyroidism (peripheral vestibular in dogs)" },
    ],
    differentials: ["Otitis media/interna", "Nasopharyngeal polyp (cat)", "Brainstem tumour / infarct", "Hypothyroidism"],
    medications: [
      { drug: "Maropitant", doseMgPerKg: 1, doseText: "1 mg/kg", route: "SC / PO", frequency: "SID", duration: "3–5 days" },
      { drug: "Meclizine (motion sickness)", doseMgPerKg: null, doseText: "25 mg/dog / 12.5 mg/cat", route: "PO", frequency: "SID", duration: "5–7 days" },
      { drug: "IV fluids (if not eating)", doseMgPerKg: null, doseText: "Maintenance", route: "IV / SC", frequency: "CRI", duration: "24–48h" },
    ],
    supportiveCare: [
      "Confine to a padded quiet area; hand-feed and hand-water.",
      "Most improve within 72h; residual head tilt may persist.",
      "Refer if worsening or non-peripheral signs develop.",
    ],
  },
  {
    id: "cds",
    name: "Cognitive Dysfunction Syndrome (CDS)",
    species: ["canine", "feline"],
    category: "Neurology",
    clinicalSigns: "DISHAA — Disorientation, altered Interactions, Sleep-wake changes, House-soiling, Activity changes, Anxiety.",
    diagnostics: [
      { test: "DISHAA / geriatric checklist", finding: "Owner questionnaire — subjective screening" },
      { test: "CBC + biochem + T4 + urinalysis + BP", finding: "Exclude systemic disease mimicking CDS" },
      { test: "MRI (if focal signs)", finding: "Rule out neoplasia" },
    ],
    differentials: ["Hearing / vision loss", "Chronic pain (arthritis)", "Hypertension", "Hyperthyroidism (cat)", "Brain tumour"],
    medications: [
      { drug: "Selegiline (Anipryl)", doseMgPerKg: 0.5, doseText: "0.5 mg/kg", route: "PO AM", frequency: "SID", duration: "Long term trial 60 days" },
      { drug: "SAMe + antioxidant senior diet (Hill's b/d)", doseMgPerKg: 20, doseText: "20 mg/kg SAMe", route: "PO", frequency: "SID", duration: "Long term" },
      { drug: "Trazodone (nocturnal restlessness)", doseMgPerKg: 5, doseText: "3–5 mg/kg", route: "PO", frequency: "SID / PRN evening", duration: "Long term" },
    ],
    supportiveCare: [
      "Environmental enrichment — night lights, familiar bedding, routine.",
      "Manage concurrent pain / sensory decline.",
      "Owner support & realistic expectations.",
    ],
  },
  {
    id: "distemper",
    name: "Canine Distemper Virus",
    species: ["canine"],
    category: "Infectious",
    clinicalSigns: "Biphasic fever, oculo-nasal discharge, GI signs, hyperkeratosis of nose/pads, myoclonus, seizures.",
    diagnostics: [
      { test: "PCR (conjunctival swab, EDTA blood, CSF)", finding: "Positive early in disease" },
      { test: "Serology (IgM / IgG)", finding: "Rising titre supports diagnosis" },
      { test: "CBC", finding: "Lymphopenia, thrombocytopenia" },
      { test: "Thoracic radiographs", finding: "Interstitial or bronchointerstitial pattern (secondary pneumonia)" },
    ],
    differentials: ["Kennel cough complex", "Parvovirus (GI signs)", "Bacterial pneumonia", "Toxoplasmosis (CNS signs)", "Meningoencephalitis"],
    medications: [
      { drug: "IV fluids + supportive care", doseMgPerKg: null, doseText: "Maintenance + losses", route: "IV", frequency: "CRI", duration: "Until stable" },
      { drug: "Doxycycline (secondary infection)", doseMgPerKg: 5, doseText: "5 mg/kg", route: "PO / IV", frequency: "BID", duration: "10–14 days" },
      { drug: "Phenobarbital (myoclonus / seizures)", doseMgPerKg: 2.5, doseText: "2.5 mg/kg", route: "PO", frequency: "BID", duration: "Long term if persistent" },
    ],
    supportiveCare: [
      "STRICT isolation, disinfection with sodium hypochlorite.",
      "Nutritional support; keep warm and hydrated.",
      "Poor prognosis if CNS signs develop.",
    ],
  },

  // ==================== OPHTHALMOLOGY ====================
  {
    id: "conjunctivitis",
    name: "Bacterial Conjunctivitis",
    species: ["canine", "feline"],
    category: "Ophthalmology",
    clinicalSigns: "Ocular discharge, blepharospasm, hyperaemia, chemosis.",
    diagnostics: [
      { test: "Fluorescein stain", finding: "Rule out corneal ulcer BEFORE steroid use" },
      { test: "Schirmer tear test", finding: "Rule out KCS" },
      { test: "Conjunctival cytology + PCR (cat)", finding: "Rule out FHV-1, Chlamydia, Mycoplasma" },
    ],
    differentials: ["Corneal ulcer", "Uveitis", "KCS", "Glaucoma", "Feline URI / Chlamydia (cat)"],
    medications: [
      { drug: "Tobramycin 0.3% ophthalmic solution", doseMgPerKg: null, doseText: "1 drop", route: "OU", frequency: "QID", duration: "7 days" },
      { drug: "Sterile saline flush", doseMgPerKg: null, doseText: "Rinse", route: "OU", frequency: "TID", duration: "7 days" },
    ],
    supportiveCare: [
      "Elizabethan collar to prevent rubbing.",
      "Recheck in 7 days if no improvement.",
    ],
  },
  {
    id: "corneal-ulcer",
    name: "Corneal Ulcer",
    species: ["canine", "feline"],
    category: "Ophthalmology",
    clinicalSigns: "Blepharospasm, photophobia, epiphora, ocular discharge, corneal opacity, miosis.",
    diagnostics: [
      { test: "Fluorescein staining", finding: "Positive uptake at defect — confirms ulcer" },
      { test: "Corneal cytology + culture", finding: "Guide antibiotic choice if melting / deep ulcer" },
      { test: "Schirmer tear test + IOP", finding: "Rule out KCS or secondary uveitis" },
      { test: "Rose bengal (herpesvirus cat)", finding: "Dendritic staining pattern" },
    ],
    differentials: ["Herpesvirus keratitis (cat)", "Foreign body", "Entropion", "Trichiasis", "KCS"],
    medications: [
      { drug: "Ofloxacin 0.3% ophthalmic", doseMgPerKg: null, doseText: "1 drop", route: "OU", frequency: "QID (q4h if severe)", duration: "Until re-epithelialised + 3 days" },
      { drug: "Atropine 1% (if reflex uveitis)", doseMgPerKg: null, doseText: "1 drop", route: "OU", frequency: "BID until mydriasis, then SID", duration: "Until pain resolves" },
      { drug: "Serum eye drops (melting ulcer)", doseMgPerKg: null, doseText: "1 drop", route: "OU", frequency: "q2h", duration: "Until healed" },
      { drug: "Famciclovir (feline herpesvirus ulcer)", doseMgPerKg: null, doseText: "90 mg/kg / 500 mg cat", route: "PO", frequency: "BID–TID", duration: "3 weeks" },
    ],
    supportiveCare: [
      "Elizabethan collar essential.",
      "NO topical steroids on any ulcer.",
      "Recheck every 3–5 days with fluorescein until healed; refer if deepening.",
    ],
  },
  {
    id: "kcs",
    name: "Keratoconjunctivitis Sicca (KCS / Dry Eye)",
    species: ["canine"],
    category: "Ophthalmology",
    clinicalSigns: "Chronic mucoid discharge, blepharospasm, corneal pigmentation / vascularisation. Cavaliers, Bulldogs, WHWTs.",
    diagnostics: [
      { test: "Schirmer tear test (STT-1)", finding: "<10 mm/min = KCS; <5 mm/min = severe" },
      { test: "Fluorescein stain", finding: "Rule out concurrent ulcer" },
      { test: "Concurrent disease screen", finding: "Rule out hypothyroidism, Cushing's, sulfonamide toxicity" },
    ],
    differentials: ["Chronic conjunctivitis", "Corneal ulcer", "Ectopic cilia / trichiasis", "Pannus"],
    medications: [
      { drug: "Cyclosporine 0.2% ointment (Optimmune)", doseMgPerKg: null, doseText: "Thin strip", route: "OU", frequency: "BID", duration: "Life-long" },
      { drug: "Tacrolimus 0.03% (refractory)", doseMgPerKg: null, doseText: "1 drop", route: "OU", frequency: "BID", duration: "Long term" },
      { drug: "Artificial tears (Hyaluronate)", doseMgPerKg: null, doseText: "1 drop", route: "OU", frequency: "QID", duration: "Life-long adjunct" },
      { drug: "Chloramphenicol (secondary infection)", doseMgPerKg: null, doseText: "1 drop", route: "OU", frequency: "QID", duration: "7 days as needed" },
    ],
    supportiveCare: [
      "Recheck STT every 3 months.",
      "Owner education — life-long therapy.",
      "Refer for parotid duct transposition if refractory.",
    ],
  },
  {
    id: "glaucoma",
    name: "Glaucoma",
    species: ["canine", "feline"],
    category: "Ophthalmology",
    clinicalSigns: "Severe ocular pain, buphthalmos, corneal oedema, episcleral congestion, blindness. Cocker, Basset, Beagle predisposed.",
    diagnostics: [
      { test: "Tonometry (Tonopen, TonoVet)", finding: "IOP >25 mmHg (>30 diagnostic; normal 10–20 mmHg)" },
      { test: "Gonioscopy", finding: "Assess iridocorneal angle — primary vs secondary" },
      { test: "Ocular ultrasound", finding: "Rule out intraocular mass, lens luxation" },
    ],
    differentials: ["Anterior uveitis (secondary glaucoma)", "Lens luxation", "Intraocular neoplasia", "Retinal detachment"],
    medications: [
      { drug: "Mannitol 20% (acute crisis)", doseMgPerKg: null, doseText: "1–2 g/kg over 15–30 min", route: "IV", frequency: "Once", duration: "Emergency reduction" },
      { drug: "Dorzolamide 2% + Timolol 0.5%", doseMgPerKg: null, doseText: "1 drop", route: "OU", frequency: "TID", duration: "Long term" },
      { drug: "Latanoprost 0.005% (dog)", doseMgPerKg: null, doseText: "1 drop", route: "OU affected", frequency: "SID–BID", duration: "Long term" },
      { drug: "Prednisolone acetate 1% (if uveitis)", doseMgPerKg: null, doseText: "1 drop", route: "OU", frequency: "QID", duration: "As uveitis dictates" },
    ],
    supportiveCare: [
      "Refer to ophthalmologist urgently.",
      "Prophylactic therapy for contralateral eye (primary glaucoma).",
      "Enucleation / cyclophotocoagulation if blind + painful.",
    ],
  },
  {
    id: "uveitis",
    name: "Anterior Uveitis",
    species: ["canine", "feline"],
    category: "Ophthalmology",
    clinicalSigns: "Photophobia, miosis, aqueous flare, hypopyon, blepharospasm, decreased IOP.",
    diagnostics: [
      { test: "Ophthalmic exam + tonometry", finding: "Low IOP, flare, keratic precipitates" },
      { test: "CBC + biochem + serology (Ehrlichia, Leishmania, Toxo, FIV/FeLV/FIP)", finding: "Systemic infectious cause" },
      { test: "Thoracic radiographs / abdominal US", finding: "Rule out neoplasia in older animals" },
      { test: "BP measurement", finding: "Rule out hypertensive retinopathy" },
    ],
    differentials: ["Infectious (Ehrlichia, Lepto, Toxo, FIP, FeLV, FIV, Blastomyces)", "Immune-mediated", "Neoplasia", "Trauma", "Idiopathic"],
    medications: [
      { drug: "Prednisolone acetate 1%", doseMgPerKg: null, doseText: "1 drop", route: "OU affected", frequency: "QID", duration: "Taper over 2–4 weeks" },
      { drug: "Atropine 1%", doseMgPerKg: null, doseText: "1 drop", route: "OU affected", frequency: "BID until mydriatic then SID", duration: "As needed for pain" },
      { drug: "Meloxicam (systemic NSAID)", doseMgPerKg: 0.1, doseText: "0.1 mg/kg", route: "PO", frequency: "SID", duration: "7–14 days" },
      { drug: "Doxycycline (if tick-borne cause)", doseMgPerKg: 10, doseText: "10 mg/kg", route: "PO", frequency: "SID", duration: "28 days" },
    ],
    supportiveCare: [
      "Investigate systemic cause — do not treat blindly.",
      "Recheck IOP + flare every 5–7 days.",
      "Elizabethan collar to prevent self-trauma.",
    ],
  },
  {
    id: "cherry-eye",
    name: "Prolapse of the Third Eyelid Gland (Cherry Eye)",
    species: ["canine"],
    category: "Ophthalmology",
    clinicalSigns: "Round pink swelling at medial canthus. Bulldogs, Beagles, Cocker Spaniels, young dogs.",
    diagnostics: [
      { test: "Physical exam", finding: "Classic prolapsed nictitans gland — confirmatory" },
      { test: "Schirmer test", finding: "Baseline; risk of KCS if gland removed" },
      { test: "Fluorescein stain", finding: "Rule out concurrent ulcer from rubbing" },
    ],
    differentials: ["Nictitans neoplasia", "Third eyelid cartilage eversion", "Conjunctival cyst"],
    medications: [
      { drug: "Chloramphenicol ophthalmic (short course pre-op)", doseMgPerKg: null, doseText: "1 drop", route: "OU", frequency: "QID", duration: "Until surgery" },
      { drug: "Prednisolone acetate 1% (reduce swelling)", doseMgPerKg: null, doseText: "1 drop", route: "OU", frequency: "TID", duration: "3 days pre-surgery" },
    ],
    supportiveCare: [
      "Surgical replacement (Morgan pocket / anchoring) — DO NOT excise.",
      "Elizabethan collar 2 weeks post-op.",
      "Recheck STT at 6 weeks to confirm normal tear production.",
    ],
  },
  {
    id: "pannus",
    name: "Chronic Superficial Keratitis (Pannus)",
    species: ["canine"],
    category: "Ophthalmology",
    clinicalSigns: "Bilateral, symmetric, vascular, pigmented plaque on lateral cornea. GSDs, high-altitude / UV exposure.",
    diagnostics: [
      { test: "Physical exam", finding: "Bilateral corneal pigmentation + vascularisation, worse in sunny climates" },
      { test: "Fluorescein stain", finding: "Rule out concurrent ulcer" },
      { test: "Biopsy (uncertain cases)", finding: "T-lymphocyte-mediated infiltrate" },
    ],
    differentials: ["KCS", "Chronic uveitis", "Corneal dystrophy", "Neoplasia"],
    medications: [
      { drug: "Cyclosporine 0.2% ointment", doseMgPerKg: null, doseText: "Thin strip", route: "OU", frequency: "BID", duration: "Life-long" },
      { drug: "Tacrolimus 0.03% (refractory)", doseMgPerKg: null, doseText: "1 drop", route: "OU", frequency: "BID", duration: "Long term" },
      { drug: "Prednisolone acetate 1% (initial flare)", doseMgPerKg: null, doseText: "1 drop", route: "OU", frequency: "QID → taper", duration: "4 weeks" },
    ],
    supportiveCare: [
      "UV protection — dog goggles (Doggles), avoid midday sun.",
      "Life-long topical therapy; recheck every 3–6 months.",
    ],
  },

  // ==================== MUSCULOSKELETAL ====================
  {
    id: "arthritis",
    name: "Osteoarthritis / Degenerative Joint Disease",
    species: ["canine", "feline"],
    category: "Musculoskeletal",
    clinicalSigns: "Stiffness, reluctance to jump / climb stairs, lameness after rest, muscle wasting.",
    diagnostics: [
      { test: "Orthopaedic exam", finding: "Joint effusion, crepitus, reduced ROM" },
      { test: "Radiographs of affected joint(s)", finding: "Osteophytes, joint effusion, subchondral sclerosis" },
      { test: "CBC + biochem (baseline for NSAIDs)", finding: "Ensure normal renal / hepatic function" },
      { test: "Synovial fluid analysis (if uncertain)", finding: "Rule out septic / immune-mediated joint disease" },
    ],
    differentials: ["Septic arthritis", "Immune-mediated polyarthritis", "Neoplasia (osteosarcoma)", "Ligamentous injury"],
    medications: [
      { drug: "Meloxicam", doseMgPerKg: 0.1, doseText: "0.1 mg/kg (dog) / 0.05 mg/kg (cat)", route: "PO", frequency: "SID", duration: "Long term with rechecks" },
      { drug: "Gabapentin", doseMgPerKg: 10, doseText: "10 mg/kg", route: "PO", frequency: "BID–TID", duration: "Long term" },
      { drug: "Glucosamine + Chondroitin", doseMgPerKg: null, doseText: "1 tab / 10 kg", route: "PO", frequency: "SID", duration: "Long term" },
      { drug: "Bedinvetmab (Librela dog) / Frunevetmab (Solensia cat)", doseMgPerKg: null, doseText: "Weight-band monthly injection", route: "SC", frequency: "Monthly", duration: "Long term" },
    ],
    supportiveCare: [
      "Weight optimisation (BCS 4–5/9).",
      "Hydrotherapy, controlled leash walks, orthopaedic bedding.",
      "Baseline & 6-monthly bloodwork on NSAIDs.",
    ],
  },
  {
    id: "cruciate-rupture",
    name: "Cranial Cruciate Ligament Rupture",
    species: ["canine"],
    category: "Musculoskeletal",
    clinicalSigns: "Acute hindlimb lameness, sitting off to the side, stifle effusion, medial buttress in chronic cases.",
    diagnostics: [
      { test: "Cranial drawer + Tibial compression tests", finding: "Positive — confirms cruciate rupture" },
      { test: "Stifle radiographs", finding: "Joint effusion, cranial tibial displacement, osteophytes (chronic)" },
      { test: "Arthroscopy (if referral)", finding: "Confirms partial vs complete tear, meniscal damage" },
    ],
    differentials: ["Meniscal injury (concurrent)", "Patellar luxation", "Osteoarthritis", "Long digital extensor avulsion"],
    medications: [
      { drug: "Carprofen or Meloxicam", doseMgPerKg: 0.1, doseText: "Carprofen 2.2 mg/kg / Meloxicam 0.1 mg/kg", route: "PO", frequency: "SID", duration: "1–2 weeks + peri-op" },
      { drug: "Gabapentin", doseMgPerKg: 10, doseText: "10 mg/kg", route: "PO", frequency: "BID–TID", duration: "2–4 weeks" },
      { drug: "Tramadol", doseMgPerKg: 5, doseText: "3–5 mg/kg", route: "PO", frequency: "TID PRN", duration: "1 week" },
    ],
    supportiveCare: [
      "Surgical stabilisation (TPLO / TTA / lateral suture) recommended for most dogs >15 kg.",
      "Strict rest 8 weeks post-op with graduated rehabilitation.",
      "Weight optimisation.",
    ],
  },
  {
    id: "hip-dysplasia",
    name: "Hip Dysplasia",
    species: ["canine"],
    category: "Musculoskeletal",
    clinicalSigns: "Bunny-hopping gait, reluctance to rise, exercise intolerance, muscle atrophy, positive Ortolani sign.",
    diagnostics: [
      { test: "OFA / PennHIP radiographs (ventrodorsal extended)", finding: "Subluxation, remodeling, incongruent hip joint" },
      { test: "Orthopaedic exam", finding: "Positive Ortolani (young), pain on hip extension" },
      { test: "CBC + biochem", finding: "Baseline for chronic NSAIDs" },
    ],
    differentials: ["Bilateral cruciate disease", "Lumbosacral disease", "Cauda equina syndrome", "Neurologic disease"],
    medications: [
      { drug: "Meloxicam or Carprofen", doseMgPerKg: 0.1, doseText: "Meloxicam 0.1 mg/kg / Carprofen 2.2 mg/kg", route: "PO", frequency: "SID", duration: "Long term" },
      { drug: "Gabapentin", doseMgPerKg: 10, doseText: "10 mg/kg", route: "PO", frequency: "BID–TID", duration: "Long term" },
      { drug: "Adequan (PSGAG)", doseMgPerKg: 4.4, doseText: "4.4 mg/kg", route: "IM", frequency: "2×/week × 4 weeks then monthly", duration: "Long term" },
      { drug: "Bedinvetmab (Librela)", doseMgPerKg: null, doseText: "Weight-band dose", route: "SC", frequency: "Monthly", duration: "Long term" },
    ],
    supportiveCare: [
      "Weight optimisation is single most important intervention.",
      "Hydrotherapy, controlled exercise.",
      "Refer for JPS (young) or THR / FHO (adult) if refractory.",
    ],
  },
  {
    id: "patellar-luxation",
    name: "Medial Patellar Luxation",
    species: ["canine", "feline"],
    category: "Musculoskeletal",
    clinicalSigns: "Intermittent skipping lameness, holding leg up momentarily. Small breed dogs (Yorkie, Pom, Chihuahua).",
    diagnostics: [
      { test: "Orthopaedic exam", finding: "Grade I–IV luxation on patellar manipulation" },
      { test: "Stifle radiographs", finding: "Assess joint changes, femoral / tibial deformity" },
    ],
    differentials: ["Cranial cruciate rupture", "Trochlear dysplasia", "Coxofemoral disease", "Long digital extensor avulsion"],
    medications: [
      { drug: "Meloxicam (during acute lameness)", doseMgPerKg: 0.1, doseText: "0.1 mg/kg", route: "PO", frequency: "SID", duration: "5–7 days PRN" },
      { drug: "Adequan (PSGAG)", doseMgPerKg: 4.4, doseText: "4.4 mg/kg", route: "IM", frequency: "2×/week × 4 weeks then monthly", duration: "Long term adjunct" },
    ],
    supportiveCare: [
      "Grade II–IV — surgical correction (trochleoplasty, TTT).",
      "Weight optimisation, controlled exercise.",
      "Post-op rehab 6–8 weeks.",
    ],
  },
  {
    id: "panosteitis",
    name: "Panosteitis ('Growing Pains')",
    species: ["canine"],
    category: "Musculoskeletal",
    clinicalSigns: "Shifting-leg lameness in young large-breed dogs (5–18 mo), pain on deep long-bone palpation, may be lethargic + febrile.",
    diagnostics: [
      { test: "Long-bone radiographs", finding: "Increased medullary opacity, patchy sclerosis" },
      { test: "Deep long-bone palpation", finding: "Focal pain reproducible on pressure" },
      { test: "CBC (rule out infection)", finding: "Usually normal; rule out osteomyelitis" },
    ],
    differentials: ["Osteomyelitis", "Hypertrophic osteodystrophy", "Elbow / hip dysplasia", "OCD lesions"],
    medications: [
      { drug: "Carprofen", doseMgPerKg: 2, doseText: "2 mg/kg", route: "PO", frequency: "BID", duration: "7–14 days during flare" },
      { drug: "Tramadol (adjunct)", doseMgPerKg: 3, doseText: "3–5 mg/kg", route: "PO", frequency: "TID PRN", duration: "As needed" },
    ],
    supportiveCare: [
      "Self-limiting — usually resolves by 18–24 mo.",
      "Restricted exercise during painful episodes.",
      "Owner reassurance — good long-term prognosis.",
    ],
  },

  // ==================== INFECTIOUS / PARASITIC ====================
  {
    id: "helminthiasis",
    name: "Intestinal Helminthiasis (Round/Hook/Whip)",
    species: ["canine", "feline"],
    category: "Parasitic",
    clinicalSigns: "Poor coat, pot-belly, weight loss, diarrhoea, visible worms in stool.",
    diagnostics: [
      { test: "Faecal flotation (zinc sulfate or sugar)", finding: "Nematode eggs" },
      { test: "Direct faecal smear", finding: "Motile larvae in fresh sample" },
      { test: "PCR panel (if high-risk / imported)", finding: "Speciation" },
    ],
    differentials: ["Giardiasis", "Coccidiosis", "Protein-losing enteropathy", "EPI", "IBD"],
    medications: [
      { drug: "Pyrantel pamoate", doseMgPerKg: 5, doseText: "5 mg/kg", route: "PO", frequency: "Repeat in 14 days", duration: "2 doses" },
      { drug: "Fenbendazole (Panacur)", doseMgPerKg: 50, doseText: "50 mg/kg", route: "PO", frequency: "SID × 3 days", duration: "3 days" },
    ],
    supportiveCare: [
      "Faecal recheck 3 weeks post-treatment.",
      "Monthly broad-spectrum dewormer schedule.",
    ],
  },
  {
    id: "tapeworm",
    name: "Cestodiasis (Tapeworm)",
    species: ["canine", "feline"],
    category: "Parasitic",
    clinicalSigns: "Rice-grain proglottids around perineum, scooting, mild pruritus.",
    diagnostics: [
      { test: "Owner report of proglottids / tape segments", finding: "Diagnostic" },
      { test: "Faecal flotation", finding: "Egg packets — low sensitivity" },
      { test: "Flea history", finding: "Fleas are intermediate host for Dipylidium" },
    ],
    differentials: ["Round/hook worms", "Anal sac disease (scooting)", "Perianal fistula"],
    medications: [
      { drug: "Praziquantel", doseMgPerKg: 5, doseText: "5 mg/kg", route: "PO / SC", frequency: "Single dose", duration: "1 dose, repeat if re-infection" },
    ],
    supportiveCare: [
      "Strict flea control.",
      "Prevent hunting / raw offal ingestion.",
    ],
  },
  {
    id: "tick-fever",
    name: "Tick-borne Ehrlichiosis / Anaplasmosis",
    species: ["canine"],
    category: "Infectious",
    clinicalSigns: "Fever, lethargy, epistaxis, petechiae, thrombocytopenia, splenomegaly.",
    diagnostics: [
      { test: "SNAP 4Dx Plus", finding: "Antigen / antibody to Ehrlichia, Anaplasma, Borrelia, heartworm" },
      { test: "CBC + platelet count", finding: "Thrombocytopenia (often marked), anaemia, leukopenia" },
      { test: "PCR (blood)", finding: "Confirms species; useful in acute phase" },
      { test: "Biochem + urinalysis", finding: "Hyperglobulinaemia, proteinuria (chronic Ehrlichia)" },
    ],
    differentials: ["Babesiosis", "IMHA / ITP", "Leptospirosis", "SLE", "Bone marrow disease"],
    medications: [
      { drug: "Doxycycline", doseMgPerKg: 10, doseText: "10 mg/kg", route: "PO", frequency: "SID", duration: "28 days" },
      { drug: "Prednisolone (if immune-mediated component)", doseMgPerKg: 1, doseText: "1 mg/kg", route: "PO", frequency: "SID, taper 3 weeks", duration: "3 weeks" },
    ],
    supportiveCare: [
      "Monitor CBC weekly during treatment.",
      "Year-round tick preventive (fluralaner / afoxolaner).",
      "Transfusion if PCV <15%.",
    ],
  },
  {
    id: "leptospirosis",
    name: "Leptospirosis",
    species: ["canine"],
    category: "Infectious",
    clinicalSigns: "Fever, lethargy, vomiting, PU/PD → oliguria, icterus, pulmonary haemorrhage. ZOONOTIC — PPE!",
    diagnostics: [
      { test: "MAT (Microscopic Agglutination Test)", finding: "4× rise in paired titres (acute + convalescent) OR single high titre" },
      { test: "PCR (blood + urine)", finding: "Positive in acute infection" },
      { test: "Biochem + urinalysis", finding: "Marked azotaemia, elevated ALT/ALP, glucosuria, granular casts" },
      { test: "Coagulation panel", finding: "Prolonged PT/PTT, thrombocytopenia" },
    ],
    differentials: ["Acute kidney injury (other causes)", "Hepatic disease", "Toxin exposure (grapes, NSAID)", "IMHA"],
    medications: [
      { drug: "Ampicillin (initial)", doseMgPerKg: 22, doseText: "22 mg/kg", route: "IV", frequency: "TID", duration: "2 weeks IV until eating" },
      { drug: "Doxycycline (eliminates carrier state)", doseMgPerKg: 5, doseText: "5 mg/kg", route: "PO", frequency: "BID", duration: "2 weeks" },
      { drug: "IV fluids (LRS)", doseMgPerKg: null, doseText: "Correct dehydration + maintenance + losses", route: "IV", frequency: "CRI", duration: "Until stable" },
      { drug: "Maropitant", doseMgPerKg: 1, doseText: "1 mg/kg", route: "IV / SC", frequency: "SID", duration: "5–7 days" },
    ],
    supportiveCare: [
      "STRICT ZOONOTIC precautions — PPE, isolate patient, disinfect environment (bleach).",
      "Refer for haemodialysis if anuric.",
      "Vaccinate at-risk dogs; consult public health for owner testing.",
    ],
  },
  {
    id: "heartworm",
    name: "Heartworm Disease (Dirofilaria immitis)",
    species: ["canine"],
    category: "Infectious",
    clinicalSigns: "Chronic cough, exercise intolerance, weight loss, right-sided heart failure. Severe: caval syndrome.",
    diagnostics: [
      { test: "Antigen test (SNAP)", finding: "Positive — female adult worms >5 mo old" },
      { test: "Modified Knott's test", finding: "Microfilariae confirms circulating microfilaraemia" },
      { test: "Thoracic radiographs", finding: "Enlarged pulmonary arteries, right heart enlargement" },
      { test: "Echocardiography", finding: "Confirms worm burden, right ventricular changes" },
    ],
    differentials: ["Cardiac disease", "Chronic bronchitis", "Bacterial pneumonia", "Neoplasia"],
    medications: [
      { drug: "Doxycycline (Wolbachia elimination)", doseMgPerKg: 10, doseText: "10 mg/kg", route: "PO", frequency: "BID", duration: "4 weeks" },
      { drug: "Melarsomine (adulticide, 3-dose protocol)", doseMgPerKg: 2.5, doseText: "2.5 mg/kg", route: "Deep IM lumbar", frequency: "Day 60, 90, 91", duration: "3 doses" },
      { drug: "Prednisolone", doseMgPerKg: 0.5, doseText: "0.5 mg/kg tapering", route: "PO", frequency: "SID → EOD × 4 weeks", duration: "4 weeks" },
      { drug: "Ivermectin (macrocyclic lactone preventive)", doseMgPerKg: null, doseText: "Heartgard chew monthly", route: "PO", frequency: "Monthly", duration: "Life-long prevention" },
    ],
    supportiveCare: [
      "STRICT cage rest 6–8 weeks after each melarsomine injection.",
      "Monitor for thromboembolism — cough, dyspnoea.",
      "Recheck antigen at 9 months post-treatment.",
    ],
  },
  {
    id: "fiv",
    name: "Feline Immunodeficiency Virus (FIV)",
    species: ["feline"],
    category: "Infectious",
    clinicalSigns: "Chronic gingivostomatitis, recurrent infections, weight loss, lymphadenopathy. Male outdoor cats.",
    diagnostics: [
      { test: "SNAP combo FeLV/FIV (antibody for FIV)", finding: "Positive antibody 60 d post-exposure" },
      { test: "Confirmatory Western blot / PCR", finding: "Rule out vaccine-induced antibody (rare)" },
      { test: "CBC", finding: "Persistent lymphopenia, neutropenia" },
    ],
    differentials: ["FeLV", "FIP", "Chronic dental disease", "GI lymphoma", "Toxoplasmosis"],
    medications: [
      { drug: "Symptomatic — treat secondary infections", doseMgPerKg: null, doseText: "Antibiotic per site of infection", route: "-", frequency: "-", duration: "As needed" },
      { drug: "L-Lysine (herpes co-infection)", doseMgPerKg: null, doseText: "250–500 mg/cat", route: "PO", frequency: "BID", duration: "Long term" },
      { drug: "Interferon-omega (Virbagen Omega, if available)", doseMgPerKg: null, doseText: "1 MU/kg", route: "SC", frequency: "SID × 5 days, 3 cycles", duration: "As per protocol" },
    ],
    supportiveCare: [
      "Indoor-only lifestyle; separate from FIV-negative cats.",
      "6-monthly wellness exams (CBC, biochem, dental).",
      "Vaccinate against common feline pathogens (avoid MLV).",
    ],
  },
  {
    id: "felv",
    name: "Feline Leukaemia Virus (FeLV)",
    species: ["feline"],
    category: "Infectious",
    clinicalSigns: "Anaemia, lymphoma, secondary infections, gingivitis, wasting. Young cats.",
    diagnostics: [
      { test: "SNAP combo (FeLV p27 antigen)", finding: "Detects circulating virus" },
      { test: "IFA / PCR (confirmation)", finding: "Distinguishes progressive vs regressive infection" },
      { test: "CBC", finding: "Non-regenerative anaemia, leukopenia" },
      { test: "Bone marrow aspirate (if cytopenic)", finding: "Assess for lymphoma / myelosuppression" },
    ],
    differentials: ["FIV", "FIP", "Lymphoma other causes", "Bartonellosis", "Toxoplasmosis"],
    medications: [
      { drug: "Symptomatic + supportive", doseMgPerKg: null, doseText: "Treat secondary conditions", route: "-", frequency: "-", duration: "Life-long" },
      { drug: "Zidovudine (AZT, off-label)", doseMgPerKg: 5, doseText: "5 mg/kg", route: "PO", frequency: "BID", duration: "Long term — monitor anaemia" },
      { drug: "Interferon-omega", doseMgPerKg: null, doseText: "1 MU/kg", route: "SC", frequency: "SID × 5 d cycles", duration: "3 cycles" },
    ],
    supportiveCare: [
      "Indoor only, isolate from negatives.",
      "Vaccinate at-risk kittens BEFORE exposure.",
      "Prognosis — progressive infection ~3 y median survival.",
    ],
  },
  {
    id: "fip",
    name: "Feline Infectious Peritonitis (FIP)",
    species: ["feline"],
    category: "Infectious",
    clinicalSigns: "Fever unresponsive to antibiotics, weight loss, ascites (effusive), granulomas (dry), uveitis, neuro signs. Young cats.",
    diagnostics: [
      { test: "Effusion analysis + Rivalta test", finding: "High-protein yellow effusion, positive Rivalta" },
      { test: "PCR for FCoV (effusion / tissue)", finding: "Positive supports diagnosis" },
      { test: "AGP + albumin:globulin ratio", finding: "A:G <0.4 suggestive; hyperglobulinaemia" },
      { test: "Histopathology / immunohistochemistry", finding: "Pyogranulomatous vasculitis — definitive" },
    ],
    differentials: ["Lymphoma", "Cardiac disease", "Bacterial peritonitis", "Toxoplasmosis", "Cryptococcus"],
    medications: [
      { drug: "GS-441524 (antiviral, unlicensed)", doseMgPerKg: 6, doseText: "6–10 mg/kg (higher for neuro/ocular)", route: "SC / PO", frequency: "SID", duration: "84 days" },
      { drug: "Prednisolone (palliative)", doseMgPerKg: 1, doseText: "1–2 mg/kg", route: "PO", frequency: "SID", duration: "Palliative only if antiviral unavailable" },
    ],
    supportiveCare: [
      "GS-441524 protocol under supervision — success rate 80–95% in confirmed cases.",
      "Nutritional support, warmth, low-stress environment.",
      "Reduce environmental FCoV load in multi-cat households.",
    ],
  },
  {
    id: "toxoplasmosis",
    name: "Toxoplasmosis",
    species: ["feline", "canine"],
    category: "Infectious",
    clinicalSigns: "Fever, hepatitis, pneumonia, uveitis, neuromuscular signs. ZOONOTIC — pregnant women avoid litter tray.",
    diagnostics: [
      { test: "Serology — IgM + IgG (paired titres)", finding: "IgM positive or 4× IgG rise supports active infection" },
      { test: "PCR (blood, CSF, aqueous humour)", finding: "Confirms in ocular / neurologic disease" },
      { test: "Faecal exam (cat)", finding: "Oocysts shed only 2–3 weeks post-primary infection" },
    ],
    differentials: ["Neospora caninum (dog)", "FIP", "Lymphoma", "Bacterial pneumonia"],
    medications: [
      { drug: "Clindamycin", doseMgPerKg: 12.5, doseText: "10–12.5 mg/kg", route: "PO", frequency: "BID", duration: "4 weeks" },
      { drug: "Trimethoprim-sulfa (alternative)", doseMgPerKg: 15, doseText: "15 mg/kg", route: "PO", frequency: "BID", duration: "4 weeks" },
    ],
    supportiveCare: [
      "Zoonotic education — pregnant / immunocompromised owners.",
      "Cat: prevent hunting, feed cooked food only.",
      "Recheck ocular / neurologic signs in 2 weeks.",
    ],
  },
  {
    id: "giardia",
    name: "Giardiasis",
    species: ["canine", "feline"],
    category: "Parasitic",
    clinicalSigns: "Chronic soft, malodorous, greasy diarrhoea ± vomiting; usually young / crowded environments.",
    diagnostics: [
      { test: "Faecal SNAP Giardia antigen", finding: "Rapid diagnosis" },
      { test: "Zinc sulfate faecal flotation × 3", finding: "Cysts intermittently shed — 3 samples over 5 days" },
      { test: "Direct smear (fresh)", finding: "Motile trophozoites" },
    ],
    differentials: ["Coccidiosis", "IBD", "Dietary intolerance", "Cryptosporidium", "Tritrichomonas foetus (cat)"],
    medications: [
      { drug: "Fenbendazole", doseMgPerKg: 50, doseText: "50 mg/kg", route: "PO", frequency: "SID", duration: "5 days" },
      { drug: "Metronidazole", doseMgPerKg: 25, doseText: "25 mg/kg", route: "PO", frequency: "BID", duration: "5 days" },
    ],
    supportiveCare: [
      "Bathe patient on last day to remove cysts from coat.",
      "Environmental disinfection (quaternary ammonium).",
      "Recheck faecal 2–4 weeks post-treatment.",
    ],
  },
  {
    id: "coccidiosis",
    name: "Coccidiosis (Cystoisospora)",
    species: ["canine", "feline"],
    category: "Parasitic",
    clinicalSigns: "Watery, sometimes bloody, diarrhoea in puppies / kittens; weight loss, dehydration.",
    diagnostics: [
      { test: "Faecal flotation", finding: "Cystoisospora oocysts" },
      { test: "Rule out concurrent Giardia + parvo", finding: "Common co-infections in shelter animals" },
    ],
    differentials: ["Parvovirus", "Giardia", "Bacterial enteritis", "Dietary indiscretion"],
    medications: [
      { drug: "Sulfadimethoxine", doseMgPerKg: 55, doseText: "55 mg/kg day 1, then 27.5 mg/kg", route: "PO", frequency: "SID", duration: "5–10 days" },
      { drug: "Ponazuril (Marquis) — off-label", doseMgPerKg: 50, doseText: "50 mg/kg", route: "PO", frequency: "Single dose", duration: "1 dose" },
    ],
    supportiveCare: [
      "Correct dehydration; probiotics support.",
      "Clean environment daily — resistant oocysts.",
      "Treat all in-contact animals if outbreak.",
    ],
  },
  {
    id: "panleukopenia",
    name: "Feline Panleukopenia (FPV)",
    species: ["feline"],
    category: "Infectious",
    clinicalSigns: "Fever, vomiting, diarrhoea, marked leukopenia, dehydration; young unvaccinated cats.",
    diagnostics: [
      { test: "Faecal parvo SNAP (cross-reacts with FPV)", finding: "Positive supports diagnosis" },
      { test: "CBC", finding: "Marked pancytopenia; WBC often <2000/µL" },
      { test: "PCR (faeces)", finding: "Definitive" },
    ],
    differentials: ["Salmonellosis", "GI foreign body", "Toxin", "FIP", "GI lymphoma"],
    medications: [
      { drug: "IV fluids (LRS + K+ supplementation)", doseMgPerKg: null, doseText: "80–120 mL/kg/day", route: "IV CRI", frequency: "Continuous", duration: "3–5 days" },
      { drug: "Ampicillin", doseMgPerKg: 22, doseText: "22 mg/kg", route: "IV", frequency: "TID", duration: "7 days" },
      { drug: "Maropitant", doseMgPerKg: 1, doseText: "1 mg/kg", route: "SC / IV", frequency: "SID", duration: "5 days" },
      { drug: "Interferon-omega", doseMgPerKg: null, doseText: "2.5 MU/kg", route: "IV", frequency: "SID × 3 days", duration: "3 days" },
    ],
    supportiveCare: [
      "Strict isolation, barrier nursing, bleach disinfection.",
      "Early enteral nutrition once vomiting controlled.",
      "Vaccinate remaining shelter / household cats.",
    ],
  },
  {
    id: "cat-bite-abscess",
    name: "Cat Bite Abscess",
    species: ["feline"],
    category: "Infectious",
    clinicalSigns: "Fluctuant subcutaneous swelling, fever, lethargy, lameness if limb involved. Outdoor tom cats.",
    diagnostics: [
      { test: "Physical exam", finding: "Localised swelling ± puncture wound, malodour" },
      { test: "FNA / lance", finding: "Purulent material — cytology + C&S if refractory" },
      { test: "FeLV / FIV SNAP", finding: "Screen for viral status after healing" },
    ],
    differentials: ["Foreign body granuloma", "Neoplasia", "Sialocoele", "Mycobacterial infection"],
    medications: [
      { drug: "Amoxicillin-Clavulanate", doseMgPerKg: 12.5, doseText: "12.5 mg/kg", route: "PO", frequency: "BID", duration: "10 days" },
      { drug: "Buprenorphine", doseMgPerKg: 0.02, doseText: "0.02 mg/kg", route: "OTM", frequency: "BID–TID", duration: "3 days" },
      { drug: "Meloxicam (short course, hydrated cat)", doseMgPerKg: 0.05, doseText: "0.05 mg/kg", route: "PO", frequency: "SID", duration: "3 days" },
    ],
    supportiveCare: [
      "Lance + flush abscess under sedation; leave open for drainage.",
      "Elizabethan collar; recheck 5–7 days.",
      "Neuter to reduce fighting; test FeLV/FIV in 6 weeks.",
    ],
  },

  // ==================== REPRODUCTIVE ====================
  {
    id: "pyometra",
    name: "Pyometra",
    species: ["canine", "feline"],
    category: "Reproductive",
    clinicalSigns: "PU/PD, vomiting, purulent vulvar discharge (open), abdominal distension, lethargy 4–8 weeks post-oestrus. Intact females.",
    diagnostics: [
      { test: "Abdominal ultrasound", finding: "Fluid-filled uterus" },
      { test: "CBC + biochem", finding: "Neutrophilic leukocytosis with left shift; azotaemia; hyperglobulinaemia" },
      { test: "Vaginal cytology", finding: "Degenerate neutrophils, intracellular bacteria" },
      { test: "Blood culture (if septic)", finding: "E. coli most common" },
    ],
    differentials: ["Uterine neoplasia", "Mucometra / hydrometra", "Peritonitis", "Ascites"],
    medications: [
      { drug: "IV fluids (LRS)", doseMgPerKg: null, doseText: "Correct shock then maintenance", route: "IV", frequency: "CRI", duration: "Peri-op" },
      { drug: "Amoxicillin-Clavulanate", doseMgPerKg: 20, doseText: "20 mg/kg", route: "IV then PO", frequency: "BID", duration: "10–14 days" },
      { drug: "Enrofloxacin (severe / septic)", doseMgPerKg: 10, doseText: "10 mg/kg", route: "IV", frequency: "SID", duration: "10 days" },
      { drug: "Aglepristone (medical mgmt — breeding bitches only)", doseMgPerKg: 10, doseText: "10 mg/kg", route: "SC", frequency: "Day 1, 2, 8", duration: "3 doses" },
    ],
    supportiveCare: [
      "Ovariohysterectomy is treatment of choice.",
      "Peri-op fluids + broad-spectrum antibiotics.",
      "Refer if compromised or unclosed cervix (medical management specialised).",
    ],
  },
  {
    id: "mastitis",
    name: "Mastitis (Post-partum)",
    species: ["canine"],
    category: "Reproductive",
    clinicalSigns: "Painful, hot, swollen mammary gland; discoloured milk, fever, poor mothering. Puppies fail to thrive.",
    diagnostics: [
      { test: "Milk cytology + culture", finding: "Neutrophils + bacteria (E. coli, Staph, Strep)" },
      { test: "CBC", finding: "Neutrophilic leukocytosis with left shift" },
      { test: "Physical exam", finding: "Firm, discoloured (purple/black) painful gland" },
    ],
    differentials: ["Galactostasis", "Mammary neoplasia", "Cellulitis", "Trauma"],
    medications: [
      { drug: "Amoxicillin-Clavulanate", doseMgPerKg: 20, doseText: "20 mg/kg", route: "PO", frequency: "BID", duration: "14–21 days" },
      { drug: "Meloxicam", doseMgPerKg: 0.1, doseText: "0.1 mg/kg", route: "PO", frequency: "SID", duration: "5–7 days" },
      { drug: "Warm compresses + gentle expression", doseMgPerKg: null, doseText: "Manual expression", route: "Topical", frequency: "QID", duration: "Until resolved" },
    ],
    supportiveCare: [
      "Continue nursing from unaffected glands (unless gangrenous).",
      "Supplement puppies if gland loss extensive.",
      "Surgical mastectomy for gangrenous mastitis.",
    ],
  },
  {
    id: "eclampsia",
    name: "Puerperal Tetany / Eclampsia (Hypocalcaemia)",
    species: ["canine"],
    category: "Reproductive",
    clinicalSigns: "Restlessness, whining, panting, muscle fasciculations, stiff gait, seizures — 1–4 weeks post-partum, small-breed nursing bitch.",
    diagnostics: [
      { test: "Ionised calcium", finding: "<0.9 mmol/L (<3.6 mg/dL) — confirms" },
      { test: "Physical exam + history", finding: "Nursing dam with tremors" },
      { test: "Blood glucose", finding: "Rule out concurrent hypoglycaemia" },
    ],
    differentials: ["Hypoglycaemia", "Epilepsy", "Toxin exposure", "Meningoencephalitis"],
    medications: [
      { drug: "Calcium gluconate 10%", doseMgPerKg: null, doseText: "0.5–1.5 mL/kg", route: "IV slow over 15–30 min", frequency: "Titrate to signs, monitor ECG", duration: "Emergency" },
      { drug: "Diazepam (if seizing)", doseMgPerKg: 0.5, doseText: "0.5 mg/kg", route: "IV", frequency: "PRN", duration: "Until stable" },
      { drug: "Calcium carbonate (oral maintenance)", doseMgPerKg: null, doseText: "50–100 mg/kg/day divided", route: "PO with food", frequency: "TID", duration: "Until weaning" },
    ],
    supportiveCare: [
      "Wean pups immediately; hand-rear on formula.",
      "Educate owner — do NOT calcium-supplement pregnant dogs.",
      "Recurrence likely with subsequent litters — plan supplementation.",
    ],
  },

  // ==================== ONCOLOGY ====================
  {
    id: "lymphoma",
    name: "Multicentric Lymphoma",
    species: ["canine", "feline"],
    category: "Oncology",
    clinicalSigns: "Generalised painless peripheral lymphadenopathy, lethargy, weight loss. Middle-aged to older animals.",
    diagnostics: [
      { test: "FNA / cytology of lymph node", finding: "Monomorphic lymphoblasts >50%" },
      { test: "Flow cytometry + PARR", finding: "B vs T immunophenotype; clonality" },
      { test: "Thoracic radiographs + abdominal US", finding: "Staging — mediastinum, spleen, liver" },
      { test: "Bone marrow aspirate", finding: "Stage V if involved" },
      { test: "FeLV / FIV (cat)", finding: "Common association in cats" },
    ],
    differentials: ["Reactive lymphadenopathy", "Ehrlichiosis", "Systemic mycosis", "Metastatic disease", "Leukaemia"],
    medications: [
      { drug: "CHOP protocol (Cyclophosphamide, Vincristine, Doxorubicin, Prednisolone)", doseMgPerKg: null, doseText: "Refer oncologist for weekly rotation", route: "IV / PO", frequency: "Weekly rotation × 19 weeks", duration: "Induction protocol" },
      { drug: "Prednisolone (single-agent palliation)", doseMgPerKg: 1, doseText: "1–2 mg/kg", route: "PO", frequency: "SID, taper", duration: "Palliative — median survival 1–3 mo" },
      { drug: "Lomustine (rescue)", doseMgPerKg: null, doseText: "60–90 mg/m² surface area", route: "PO", frequency: "Every 3 weeks", duration: "Per protocol" },
    ],
    supportiveCare: [
      "Refer to oncology specialist — CHOP median survival 12–14 mo (dog).",
      "Monitor CBC before every chemotherapy cycle.",
      "Discuss quality-of-life goals with owner.",
    ],
  },
  {
    id: "mct",
    name: "Cutaneous Mast Cell Tumour",
    species: ["canine"],
    category: "Oncology",
    clinicalSigns: "Solitary or multiple cutaneous / subcutaneous nodule(s), often erythematous, may fluctuate ('Darier's sign'). Boxers, Bulldogs.",
    diagnostics: [
      { test: "FNA cytology", finding: "Round cells with basophilic granules — MCT confirmed" },
      { test: "Excisional biopsy + histopathology", finding: "Patnaik / Kiupel grading; margins" },
      { test: "Lymph node aspirate + abdominal US", finding: "Staging — regional LN + spleen/liver" },
      { test: "c-KIT PCR mutation analysis", finding: "Guides toceranib eligibility" },
    ],
    differentials: ["Histiocytoma", "Lipoma", "Lymphoma", "Melanoma", "Insect bite reaction"],
    medications: [
      { drug: "Wide surgical excision (3 cm lateral / 1 fascial plane deep)", doseMgPerKg: null, doseText: "Surgery", route: "-", frequency: "-", duration: "Once" },
      { drug: "Toceranib phosphate (Palladia)", doseMgPerKg: 3, doseText: "2.5–3.25 mg/kg", route: "PO", frequency: "EOD or M/W/F", duration: "Long term (unresectable / metastatic)" },
      { drug: "Prednisolone + Vinblastine (chemo)", doseMgPerKg: 0.5, doseText: "Pred 0.5 mg/kg + Vin 2 mg/m²", route: "PO / IV", frequency: "Weekly × 4 then EOW", duration: "12 doses" },
      { drug: "Diphenhydramine + Famotidine (H1 + H2 block)", doseMgPerKg: 2, doseText: "Diphenhyd 2 mg/kg + Famot 0.5 mg/kg", route: "PO", frequency: "BID", duration: "Long term" },
    ],
    supportiveCare: [
      "Peri-op steroids + antihistamines to prevent degranulation.",
      "Grade-based prognosis; refer for radiation if incomplete margins.",
      "Regular skin exams (self-exam by owner).",
    ],
  },
  {
    id: "osteosarcoma",
    name: "Appendicular Osteosarcoma",
    species: ["canine"],
    category: "Oncology",
    clinicalSigns: "Progressive lameness, firm painful long-bone swelling — distal radius, proximal humerus, distal femur/tibia. Large / giant breeds.",
    diagnostics: [
      { test: "Radiographs affected bone (2 views)", finding: "Sunburst / Codman's triangle / mixed lytic-productive lesion" },
      { test: "FNA cytology / bone biopsy", finding: "Confirms mesenchymal neoplasia" },
      { test: "Thoracic radiographs (3 views)", finding: "Staging — pulmonary metastases in 10% at dx" },
      { test: "Alkaline phosphatase", finding: "Elevated ALP negative prognostic factor" },
    ],
    differentials: ["Chondrosarcoma", "Fibrosarcoma", "Fungal osteomyelitis", "Bacterial osteomyelitis", "Bone cyst"],
    medications: [
      { drug: "Amputation OR limb-spare + carboplatin chemo", doseMgPerKg: null, doseText: "Surgical", route: "-", frequency: "-", duration: "Once" },
      { drug: "Carboplatin (adjuvant chemo)", doseMgPerKg: null, doseText: "300 mg/m²", route: "IV", frequency: "Every 3 weeks × 4–6", duration: "Chemo protocol" },
      { drug: "Palliative — pamidronate + tramadol + NSAID", doseMgPerKg: 1, doseText: "Pamidronate 1–2 mg/kg", route: "IV", frequency: "Every 4 weeks", duration: "Palliative" },
    ],
    supportiveCare: [
      "Pathologic fracture risk — restrict activity.",
      "Median survival — amputation alone 4 mo; + chemo 10–12 mo.",
      "Discuss QoL, consider palliative radiation.",
    ],
  },

  // ==================== TOXICOLOGY ====================
  {
    id: "chocolate-tox",
    name: "Chocolate Toxicity",
    species: ["canine"],
    category: "Toxicology",
    clinicalSigns: "Vomiting, hyperactivity, tachycardia, tremors, seizures, arrhythmias. Dose-dependent (theobromine + caffeine).",
    diagnostics: [
      { test: "History — type + quantity of chocolate", finding: "Dark / baking > milk chocolate. >20 mg/kg mild, >40 severe" },
      { test: "ECG", finding: "Tachyarrhythmias — VPCs, VTach" },
      { test: "Physical exam", finding: "Restlessness, tachycardia, hyperthermia" },
    ],
    differentials: ["Caffeine toxicity", "Amphetamine ingestion", "Xylitol toxicity", "Serotonin syndrome"],
    medications: [
      { drug: "Apomorphine (emesis, <2h ingestion)", doseMgPerKg: 0.03, doseText: "0.03 mg/kg", route: "IV or conjunctival", frequency: "Once", duration: "Once — decontamination" },
      { drug: "Activated charcoal (with sorbitol)", doseMgPerKg: 2, doseText: "1–3 g/kg", route: "PO", frequency: "Once, then 1 g/kg q4–6h × 24h", duration: "Enterohepatic recirculation" },
      { drug: "IV fluids", doseMgPerKg: null, doseText: "60–120 mL/kg/day", route: "IV", frequency: "CRI", duration: "24–48h" },
      { drug: "Diazepam (tremors / seizures)", doseMgPerKg: 0.5, doseText: "0.5 mg/kg", route: "IV", frequency: "PRN", duration: "Until controlled" },
      { drug: "Lidocaine (VPCs)", doseMgPerKg: 2, doseText: "2 mg/kg then CRI 25–75 µg/kg/min", route: "IV", frequency: "Bolus then CRI", duration: "Until stable" },
    ],
    supportiveCare: [
      "Urinary catheter — theobromine is reabsorbed from bladder wall.",
      "Monitor ECG 12–24h for late arrhythmias.",
      "Owner education on chocolate storage.",
    ],
  },
  {
    id: "xylitol-tox",
    name: "Xylitol Toxicity",
    species: ["canine"],
    category: "Toxicology",
    clinicalSigns: "Vomiting, weakness, ataxia, collapse, seizures — hypoglycaemia within 30–60 min. Hepatic failure 24–72h later.",
    diagnostics: [
      { test: "Blood glucose (immediate + q1–2h × 24h)", finding: "Hypoglycaemia <3.3 mmol/L" },
      { test: "Liver enzymes (baseline + q24h × 72h)", finding: "ALT / bilirubin rise 24–72h post-ingestion" },
      { test: "Coagulation (PT/PTT)", finding: "Prolongation if hepatic failure" },
    ],
    differentials: ["Insulinoma", "Sepsis", "Addison's disease", "Hepatic failure other causes"],
    medications: [
      { drug: "50% Dextrose bolus", doseMgPerKg: null, doseText: "1 mL/kg diluted 1:4", route: "IV", frequency: "Bolus, then 2.5–5% CRI", duration: "12–24h until eating" },
      { drug: "IV fluids + dextrose", doseMgPerKg: null, doseText: "Maintenance + 2.5–5% dextrose", route: "IV CRI", frequency: "Continuous", duration: "24–48h" },
      { drug: "N-Acetylcysteine (hepatoprotection)", doseMgPerKg: 140, doseText: "140 mg/kg loading then 70 mg/kg q6h × 7 doses", route: "PO / IV", frequency: "As protocol", duration: "48h" },
      { drug: "SAMe + Silybin", doseMgPerKg: 20, doseText: "20 mg/kg SAMe", route: "PO", frequency: "SID", duration: "2–4 weeks" },
    ],
    supportiveCare: [
      "Emesis contraindicated if already hypoglycaemic — go straight to dextrose.",
      "Activated charcoal NOT effective for xylitol.",
      "Hospitalise 24–72h to monitor for hepatic failure.",
    ],
  },
  {
    id: "rodenticide",
    name: "Anticoagulant Rodenticide Toxicity",
    species: ["canine", "feline"],
    category: "Toxicology",
    clinicalSigns: "Lethargy, pale mucous membranes, dyspnoea, bleeding (pulmonary, GI, subcutaneous). Onset 3–5 days post-ingestion.",
    diagnostics: [
      { test: "PT / PTT", finding: "Marked prolongation of PT initially (factor VII shortest half-life)" },
      { test: "CBC", finding: "Anaemia (regenerative), possible thrombocytopenia" },
      { test: "PIVKA test (if available)", finding: "Specific for vitamin K antagonism" },
      { test: "Thoracic radiographs / US", finding: "Pulmonary / pleural / abdominal haemorrhage" },
    ],
    differentials: ["Immune-mediated bleeding disorders", "DIC", "Angiostrongylus (dog)", "Congenital coagulopathy"],
    medications: [
      { drug: "Vitamin K1", doseMgPerKg: 2.5, doseText: "2.5–5 mg/kg (loading)", route: "SC / PO with fatty food", frequency: "Divided BID", duration: "3–4 weeks (long-acting rodenticides)" },
      { drug: "Fresh whole blood / plasma (if bleeding)", doseMgPerKg: null, doseText: "10–20 mL/kg", route: "IV", frequency: "Once, repeat PRN", duration: "PRN until clotting normalises" },
      { drug: "Activated charcoal (<8h post-ingestion)", doseMgPerKg: 2, doseText: "1–3 g/kg", route: "PO", frequency: "Once", duration: "Once" },
    ],
    supportiveCare: [
      "Recheck PT 48–72h after Vit K stopped — if still prolonged, extend 2 more weeks.",
      "Rest, minimise trauma while coagulopathic.",
      "Owner rodenticide storage education.",
    ],
  },
  {
    id: "lily-tox",
    name: "Lily Toxicity (Feline)",
    species: ["feline"],
    category: "Toxicology",
    clinicalSigns: "Vomiting, salivation, anorexia within 2h → acute kidney injury 24–72h post-ingestion. Any part of Lilium/Hemerocallis species.",
    diagnostics: [
      { test: "History of lily exposure", finding: "Any true lily (Lilium/Hemerocallis) ingestion / pollen on coat" },
      { test: "Serial creatinine + SDMA + BUN", finding: "Rising values 24–72h — anuric AKI" },
      { test: "Urinalysis", finding: "Casts, isosthenuria, glucosuria" },
    ],
    differentials: ["Other nephrotoxin (ethylene glycol, NSAID)", "Ureteral obstruction", "Pyelonephritis"],
    medications: [
      { drug: "Apomorphine (dogs only — NOT effective in cats)", doseMgPerKg: null, doseText: "Xylazine 0.44 mg/kg IM for feline emesis", route: "IM", frequency: "Once", duration: "<3h post-ingestion" },
      { drug: "Activated charcoal", doseMgPerKg: 2, doseText: "1–3 g/kg", route: "PO", frequency: "Once", duration: "Once if <6h ingestion" },
      { drug: "IV fluids (aggressive diuresis)", doseMgPerKg: null, doseText: "2× maintenance", route: "IV CRI", frequency: "Continuous", duration: "48–72h" },
      { drug: "Maropitant", doseMgPerKg: 1, doseText: "1 mg/kg", route: "SC", frequency: "SID", duration: "5 days" },
    ],
    supportiveCare: [
      "Aggressive early fluid diuresis dramatically improves prognosis.",
      "Haemodialysis referral if oliguric / anuric.",
      "Owner education — remove ALL lily species from cat households.",
    ],
  },

  // ==================== MISCELLANEOUS ====================
  {
    id: "fever-unknown",
    name: "Pyrexia of Unknown Origin (Supportive)",
    species: ["canine", "feline"],
    category: "Supportive",
    clinicalSigns: "Fever >39.5 °C, lethargy, inappetence — while workup pending.",
    diagnostics: [
      { test: "Complete CBC + biochem + urinalysis + urine culture", finding: "Baseline organ + infection screening" },
      { test: "Thoracic radiographs + abdominal US", finding: "Occult foci — mass, effusion, lymphadenopathy" },
      { test: "Infectious disease panel (Ehrlichia, Bartonella, tick panel, FIV/FeLV)", finding: "Geography-specific screening" },
      { test: "Joint tap + LN FNA (if arthralgia / lymphadenopathy)", finding: "IMPA vs septic vs neoplasia" },
    ],
    differentials: ["Infection (any site)", "Immune-mediated disease", "Neoplasia", "Drug reaction", "Endocrine (Addison's)"],
    medications: [
      { drug: "Meloxicam", doseMgPerKg: 0.1, doseText: "0.1 mg/kg (dog) / 0.05 mg/kg (cat single)", route: "PO / SC", frequency: "SID", duration: "3 days max" },
      { drug: "Amoxicillin-Clavulanate (empirical)", doseMgPerKg: 12.5, doseText: "12.5 mg/kg", route: "PO", frequency: "BID", duration: "Pending culture" },
    ],
    supportiveCare: [
      "Complete diagnostic workup before empirical steroids.",
      "IV fluids if inappetent >24h.",
      "Reassess every 48h; escalate diagnostics if no improvement.",
    ],
  },
  {
    id: "anaphylaxis",
    name: "Anaphylaxis / Acute Hypersensitivity",
    species: ["canine", "feline"],
    category: "Emergency",
    clinicalSigns: "Facial swelling, urticaria, vomiting, diarrhoea, collapse, hypotension. Onset within minutes of trigger (vaccine, insect sting, food).",
    diagnostics: [
      { test: "History + physical exam", finding: "Recent exposure to allergen" },
      { test: "Blood pressure + heart rate", finding: "Hypotension, tachycardia, weak pulses" },
      { test: "Abdominal US (dog)", finding: "'Gallbladder halo sign' — pathognomonic in anaphylactic dog" },
    ],
    differentials: ["Septic shock", "Cardiogenic shock", "Hypovolaemic shock", "Toxin exposure"],
    medications: [
      { drug: "Epinephrine (1:1000)", doseMgPerKg: 0.01, doseText: "0.01 mg/kg = 0.01 mL/kg of 1:1000", route: "IM (thigh) / IV (arrest)", frequency: "Repeat q5–10 min PRN", duration: "Immediate" },
      { drug: "IV fluids (crystalloid bolus)", doseMgPerKg: null, doseText: "20 mL/kg over 15 min, repeat PRN", route: "IV", frequency: "Bolus", duration: "Until BP restored" },
      { drug: "Diphenhydramine", doseMgPerKg: 2, doseText: "2 mg/kg", route: "IM / slow IV", frequency: "Once, then BID × 3 days", duration: "3–5 days" },
      { drug: "Dexamethasone SP", doseMgPerKg: 0.1, doseText: "0.1–0.2 mg/kg", route: "IV / IM", frequency: "Once", duration: "One dose" },
      { drug: "Oxygen therapy", doseMgPerKg: null, doseText: "40–60% FiO2", route: "Mask / cage", frequency: "Continuous", duration: "Until stable" },
    ],
    supportiveCare: [
      "Hospitalise 12–24h — biphasic response possible.",
      "Identify + avoid trigger; premedicate for future exposures.",
      "Discuss owner-carry epinephrine autoinjector (specialist cases).",
    ],
  },
  {
    id: "heatstroke",
    name: "Heatstroke",
    species: ["canine"],
    category: "Emergency",
    clinicalSigns: "Rectal temp >41 °C, panting, hypersalivation, collapse, petechiae, altered mentation, bloody diarrhoea. Brachycephalics + working dogs.",
    diagnostics: [
      { test: "Rectal temperature", finding: ">41 °C — critical" },
      { test: "CBC + biochem + coagulation + lactate", finding: "Elevated PCV, DIC evolving, elevated ALT / AST, lactate acidosis" },
      { test: "Urinalysis", finding: "Myoglobinuria, casts (renal tubular damage)" },
      { test: "ECG", finding: "Ventricular arrhythmias" },
    ],
    differentials: ["Septic shock", "Anaphylaxis", "Toxin exposure", "Malignant hyperthermia (drug-induced)"],
    medications: [
      { drug: "Active cooling (tepid water + fan) STOP at 39.4 °C", doseMgPerKg: null, doseText: "Tepid — NOT ice water", route: "External", frequency: "Continuous", duration: "Until 39.4 °C" },
      { drug: "IV fluids (LRS)", doseMgPerKg: null, doseText: "20 mL/kg boluses × 2–3 as needed", route: "IV", frequency: "Bolus", duration: "Then maintenance" },
      { drug: "Mannitol (cerebral oedema)", doseMgPerKg: null, doseText: "0.5–1 g/kg over 20 min", route: "IV", frequency: "PRN", duration: "If neurologic signs" },
      { drug: "Ampicillin-sulbactam (GI translocation cover)", doseMgPerKg: 30, doseText: "30 mg/kg", route: "IV", frequency: "TID", duration: "3–5 days" },
      { drug: "Fresh frozen plasma (if DIC)", doseMgPerKg: null, doseText: "10–20 mL/kg", route: "IV", frequency: "PRN", duration: "PRN" },
    ],
    supportiveCare: [
      "Cool with tepid water + fan — do NOT immerse in ice.",
      "Monitor for AKI, DIC, GI bleeding, cerebral oedema 48–72h.",
      "Owner education — never leave in hot vehicle; avoid mid-day exercise.",
    ],
  },
  {
    id: "gingivostomatitis",
    name: "Feline Chronic Gingivostomatitis (FCGS)",
    species: ["feline"],
    category: "Dental",
    clinicalSigns: "Ptyalism, halitosis, dysphagia, weight loss, pawing at mouth. Diffuse severe caudal stomatitis.",
    diagnostics: [
      { test: "Oral exam under anaesthesia + dental radiographs", finding: "Diffuse ulcerative caudal stomatitis, resorptive lesions" },
      { test: "Oral biopsy", finding: "Rule out squamous cell carcinoma" },
      { test: "FeLV / FIV / Calicivirus PCR", finding: "Underlying viral association" },
    ],
    differentials: ["Squamous cell carcinoma", "Eosinophilic granuloma", "Uraemic stomatitis", "Autoimmune stomatitis"],
    medications: [
      { drug: "Full-mouth or caudal extraction (dental treatment of choice)", doseMgPerKg: null, doseText: "Surgical", route: "-", frequency: "Once", duration: "Definitive" },
      { drug: "Amoxicillin-Clavulanate", doseMgPerKg: 12.5, doseText: "12.5 mg/kg", route: "PO", frequency: "BID", duration: "10–14 days" },
      { drug: "Buprenorphine", doseMgPerKg: 0.02, doseText: "0.02 mg/kg", route: "OTM", frequency: "TID", duration: "Long term PRN" },
      { drug: "Cyclosporine (refractory post-extraction)", doseMgPerKg: 3, doseText: "2.5–5 mg/kg", route: "PO", frequency: "SID", duration: "8 weeks trial" },
      { drug: "Interferon-omega (topical + SC)", doseMgPerKg: null, doseText: "0.1 MU/cat", route: "SC / oral", frequency: "SID", duration: "3 months trial" },
    ],
    supportiveCare: [
      "Home dental hygiene once post-op healed.",
      "Soft palatable food during flare.",
      "Owner counselling — refractory cases 20% even after full-mouth extraction.",
    ],
  },
  {
    id: "imha",
    name: "Immune-Mediated Haemolytic Anaemia (IMHA)",
    species: ["canine"],
    category: "Haematology",
    clinicalSigns: "Lethargy, pale mucous membranes, icterus, tachycardia, tachypnoea, pigmenturia. Cockers, Poodles predisposed.",
    diagnostics: [
      { test: "CBC + reticulocyte count", finding: "Regenerative anaemia, spherocytes on smear" },
      { test: "Saline agglutination test", finding: "Auto-agglutination — supports IMHA" },
      { test: "Coombs test", finding: "Positive supports IMHA (may be negative in 30%)" },
      { test: "Rule out triggers — SNAP 4Dx, imaging, drug history", finding: "Neoplasia, tick-borne, MMV, drug reaction" },
      { test: "Coagulation profile + d-dimers", finding: "Hypercoagulable state common; monitor for PTE" },
    ],
    differentials: ["Zinc toxicity", "Tick-borne haemolysis (Babesia, Anaplasma)", "Onion / garlic toxicity", "Microangiopathy", "Haemophagic HS"],
    medications: [
      { drug: "Prednisolone", doseMgPerKg: 2, doseText: "2 mg/kg (max 60 mg/day)", route: "PO", frequency: "SID", duration: "Taper over 6+ months based on PCV" },
      { drug: "Mycophenolate mofetil (2nd-line)", doseMgPerKg: 10, doseText: "10 mg/kg", route: "PO", frequency: "BID", duration: "Adjunct — reduces steroid dose" },
      { drug: "Clopidogrel (thromboprophylaxis)", doseMgPerKg: 2, doseText: "1–3 mg/kg", route: "PO", frequency: "SID", duration: "Until PCV / prednisolone tapered" },
      { drug: "Packed RBC transfusion", doseMgPerKg: null, doseText: "10–15 mL/kg", route: "IV", frequency: "PRN PCV <15%", duration: "PRN" },
    ],
    supportiveCare: [
      "Monitor PCV, reticulocytes, bilirubin every 3–7 days initially.",
      "Screen for thromboembolism — pulmonary + splenic vein.",
      "Prognosis guarded — 30–70% survival at 1 year.",
    ],
  },
  {
    id: "itp",
    name: "Immune-Mediated Thrombocytopenia (ITP)",
    species: ["canine"],
    category: "Haematology",
    clinicalSigns: "Petechiae, ecchymoses, epistaxis, haematuria, GI bleeding. Platelet count often <20 × 10⁹/L.",
    diagnostics: [
      { test: "CBC + platelet count + smear", finding: "Severe thrombocytopenia, rule out clumping" },
      { test: "Coagulation profile", finding: "PT/PTT normal (rules out DIC / rodenticide)" },
      { test: "Bone marrow aspirate (if refractory / non-regenerative)", finding: "Megakaryocytic hyperplasia in ITP" },
      { test: "Infectious disease screen (Ehrlichia, Anaplasma, RMSF)", finding: "Common secondary cause" },
    ],
    differentials: ["Tick-borne thrombocytopenia", "DIC", "Rodenticide toxicity", "Bone marrow disease", "Splenic disease"],
    medications: [
      { drug: "Prednisolone", doseMgPerKg: 2, doseText: "2 mg/kg", route: "PO", frequency: "SID", duration: "Taper based on platelet count over 3–6 months" },
      { drug: "Vincristine (single dose, boost platelets)", doseMgPerKg: null, doseText: "0.02 mg/kg", route: "IV slow", frequency: "Once", duration: "Once if bleeding severe" },
      { drug: "hIVIG (refractory)", doseMgPerKg: 0.5, doseText: "0.5–1 g/kg over 6h", route: "IV", frequency: "Once", duration: "If life-threatening bleed" },
      { drug: "Doxycycline (empirical if tick-endemic)", doseMgPerKg: 10, doseText: "10 mg/kg", route: "PO", frequency: "SID", duration: "28 days" },
    ],
    supportiveCare: [
      "Cage rest — avoid trauma, IM injections, jugular sticks.",
      "Recheck platelets every 24–48h until stable.",
      "Splenectomy consideration if refractory > 4 weeks.",
    ],
  },
];
